import React, { useMemo } from 'react'
import { Select, SIZE_MEDIUM } from '@mds/mds-reactjs-library'
import { numberToWords } from '../../../Utility/SharedUtility'
import {
  buildGeographyHierarchy,
  parseGeographySelectionToUiState,
  resolveGeographyApiAttributeIds,
} from './liveDashboardGeography'
import Wrapper, { GeographyMultiSelectMenuFix } from './LiveDashboardGeographyFilter.styles'
import { removeParentsWithNoSelectedChildren } from './liveDashboardDimensionUtils'

const selectPortalProps =
  typeof document !== 'undefined'
    ? { menuPortalTarget: document.body, menuPosition: 'fixed', maxMenuHeight: 280 }
    : {}

const GeographyCollapsedMultiValue = (props) => {
  const { index, selectProps, innerProps } = props
  const selected = Array.isArray(selectProps?.value) ? selectProps.value : []

  if (index !== 0 || selected.length === 0) {
    return null
  }

  const labelText = selected.map((option) => String(option?.label ?? '')).filter(Boolean).join(', ')

  return (
    <div {...innerProps} className="geography-collapsed-multi-value" title={labelText}>
      <span className="geography-collapsed-multi-value__text">{labelText}</span>
    </div>
  )
}

const geographyMultiSelectProps = {
  ...selectPortalProps,
  collapseTags: true,
  hideSelectedOptions: false,
  closeMenuOnSelect: false,
}

const geographyMultiSelectComponents = {
  MultiValue: GeographyCollapsedMultiValue,
}

const normalizeId = (id) => String(id ?? '')

const idsMatch = (left, right) => normalizeId(left) === normalizeId(right)

const LiveDashboardGeographyFilter = ({
  dimension,
  selection,
  setSelection,
  setValidationMessage,
  dimensionConfiguration,
}) => {
  const hierarchy = useMemo(() => buildGeographyHierarchy(dimension), [dimension])

  const uiState = useMemo(
    () => parseGeographySelectionToUiState(selection?.Attributes, hierarchy),
    [selection?.Attributes, hierarchy],
  )

  const regionOptions = useMemo(() => {
    const withCountries = hierarchy.regionsWithCountries.map((region) => ({
      label: region.name,
      value: normalizeId(region.id),
    }))
    const regionOnly = hierarchy.regionOnlyNodes.map((region) => ({
      label: region.name,
      value: normalizeId(region.id),
    }))
    return [...withCountries, ...regionOnly]
  }, [hierarchy])

  const countryOptions = useMemo(() => {
    const options = []
    for (const regionId of uiState.selectedRegionIds) {
      const region = hierarchy.regionsWithCountries.find((entry) => idsMatch(entry.id, regionId))
      if (!region) {
        continue
      }
      for (const country of region.countries) {
        options.push({
          label: country.name,
          value: normalizeId(country.id),
          group: region.name,
        })
      }
    }
    return options
  }, [hierarchy, uiState.selectedRegionIds])

  const hasRegionWithCountriesSelected = useMemo(
    () =>
      hierarchy.regionsWithCountries.some((region) =>
        uiState.selectedRegionIds.some((regionId) => idsMatch(regionId, region.id)),
      ),
    [hierarchy.regionsWithCountries, uiState.selectedRegionIds],
  )

  const validateAndApply = (nextRegionIds, nextCountryIds) => {
    setValidationMessage('')

    const resolvedAttributes = resolveGeographyApiAttributeIds(
      nextRegionIds,
      nextCountryIds,
      hierarchy,
    )

    const minSelections = Number.isFinite(dimensionConfiguration?.MinSelections)
      ? dimensionConfiguration.MinSelections
      : 0
    const maxSelections = Number.isFinite(dimensionConfiguration?.MaxSelections)
      ? dimensionConfiguration.MaxSelections
      : Number.MAX_SAFE_INTEGER

    if (resolvedAttributes.length < minSelections) {
      setValidationMessage(
        `Please select at least ${numberToWords(minSelections)} ${
          minSelections === 1 ? 'item' : 'items'
        } for ${dimension.DisplayName}`,
      )
    } else if (resolvedAttributes.length > maxSelections) {
      setValidationMessage(
        `Filter limit exceeded. Select up to ${numberToWords(maxSelections)} ${
          maxSelections === 1 ? 'item' : 'items'
        } for ${dimension.DisplayName}`,
      )
      return
    }

    setSelection(dimension.DimensionID, resolvedAttributes)
  }

  const resolveRegionIdsFromValues = (values) =>
    (values ?? []).map((value) => {
      const match =
        hierarchy.regionsWithCountries.find((region) => idsMatch(region.id, value)) ??
        hierarchy.regionOnlyNodes.find((region) => idsMatch(region.id, value))
      return match?.id ?? value
    })

  const onRegionChange = (values) => {
    const nextRegionIds = resolveRegionIdsFromValues(values)

    const previousRegionIdSet = new Set(uiState.selectedRegionIds.map(normalizeId))
    const nextRegionIdSet = new Set(nextRegionIds.map(normalizeId))

    let nextCountryIds = uiState.selectedCountryIds.filter((countryId) => {
      const parentRegion = hierarchy.regionsWithCountries.find((region) =>
        region.countries.some((country) => idsMatch(country.id, countryId)),
      )
      if (!parentRegion) {
        return true
      }
      return nextRegionIdSet.has(normalizeId(parentRegion.id))
    })

    for (const regionId of nextRegionIds) {
      if (previousRegionIdSet.has(normalizeId(regionId))) {
        continue
      }

      const region = hierarchy.regionsWithCountries.find((entry) => idsMatch(entry.id, regionId))
      if (!region) {
        continue
      }

      const nextCountryIdSet = new Set(nextCountryIds.map(normalizeId))
      for (const country of region.countries) {
        if (!nextCountryIdSet.has(normalizeId(country.id))) {
          nextCountryIds.push(country.id)
        }
      }
    }

    validateAndApply(nextRegionIds, nextCountryIds)
  }

  const onCountryChange = (values) => {
    const nextCountryIds = (values ?? []).map((value) => {
      for (const region of hierarchy.regionsWithCountries) {
        const match = region.countries.find((country) => idsMatch(country.id, value))
        if (match) {
          return match.id
        }
      }
      return value
    })

    const nextRegionIdSet = new Set(uiState.selectedRegionIds.map(normalizeId))
    for (const countryId of nextCountryIds) {
      const parentRegion = hierarchy.regionsWithCountries.find((region) =>
        region.countries.some((country) => idsMatch(country.id, countryId)),
      )
      if (parentRegion) {
        nextRegionIdSet.add(normalizeId(parentRegion.id))
      }
    }

    const nextRegionIds = removeParentsWithNoSelectedChildren(
      [...nextRegionIdSet].map((regionKey) => {
        const match =
          hierarchy.regionsWithCountries.find((region) => idsMatch(region.id, regionKey)) ??
          hierarchy.regionOnlyNodes.find((region) => idsMatch(region.id, regionKey))
        return match?.id ?? regionKey
      }),
      nextCountryIds,
      hierarchy.regionsWithCountries.map((region) => ({
        id: region.id,
        children: region.countries,
      })),
    )

    validateAndApply(nextRegionIds, nextCountryIds)
  }

  const regionSelectValue = uiState.selectedRegionIds.map(normalizeId)
  const countrySelectValue = uiState.selectedCountryIds.map(normalizeId)

  return (
    <Wrapper>
      <GeographyMultiSelectMenuFix />
      <div className="geography-range-row">
        <div className="geography-range-field">
          <label htmlFor={`live-dashboard-region-${dimension.DimensionID}`}>Region</label>
          <Select
            inputId={`live-dashboard-region-${dimension.DimensionID}`}
            custom
            multi
            searchable
            clearable
            placeholder="Select region(s)"
            size={SIZE_MEDIUM}
            width="100%"
            value={regionSelectValue}
            options={regionOptions}
            onChange={onRegionChange}
            components={geographyMultiSelectComponents}
            {...geographyMultiSelectProps}
          />
        </div>
        <div className="geography-range-field">
          <label htmlFor={`live-dashboard-country-${dimension.DimensionID}`}>Country</label>
          <Select
            inputId={`live-dashboard-country-${dimension.DimensionID}`}
            custom
            multi
            searchable
            clearable
            placeholder={
              hasRegionWithCountriesSelected
                ? 'Select country(ies)'
                : 'Select a region with countries first'
            }
            size={SIZE_MEDIUM}
            width="100%"
            value={countrySelectValue}
            options={countryOptions}
            onChange={onCountryChange}
            disabled={!hasRegionWithCountriesSelected}
            components={geographyMultiSelectComponents}
            {...geographyMultiSelectProps}
          />
        </div>
      </div>
    </Wrapper>
  )
}

export default LiveDashboardGeographyFilter
